import { useMemo, useState } from 'react';
import { Alert, Empty, Input, Segmented, Spin, Table, Tag, Typography } from 'antd';
import type { VariableHygieneEntry } from '@core/entities/VariableHygiene';
import { useVariableHygiene } from '@presentation/hooks/useVariableHygiene';

interface VariableHygienePanelProps {
  workspaceId: string;
}

const SCOPE_COLOR: Record<VariableHygieneEntry['scope'], string> = {
  global: 'blue',
  environment: 'purple',
  collection: 'gold',
};

type Filter = 'all' | 'unused' | 'duplicate';

export const VariableHygienePanel = ({ workspaceId }: VariableHygienePanelProps) => {
  const { data, isLoading } = useVariableHygiene(workspaceId, true);
  const [filter, setFilter] = useState<Filter>('all');

  const filtered = useMemo(() => {
    if (!data) return [];
    if (filter === 'unused') return data.entries.filter((entry) => entry.isUnused);
    if (filter === 'duplicate')
      return data.entries.filter((entry) => entry.alsoDefinedIn.length > 0);
    return data.entries;
  }, [data, filter]);

  const unusedCount = data?.entries.filter((entry) => entry.isUnused).length ?? 0;
  const duplicateCount =
    data?.entries.filter((entry) => entry.alsoDefinedIn.length > 0).length ?? 0;
  const resolvedRows = data ? Object.entries(data.resolvedPreview) : [];

  if (isLoading) {
    return (
      <div className="flex justify-center py-8">
        <Spin size="small" />
      </div>
    );
  }

  if (!data || data.entries.length === 0) {
    return (
      <Empty description="No variables defined yet — add some in Globals or an environment first" />
    );
  }

  return (
    <div className="flex flex-col gap-4">
      <div>
        <h4 className="mb-1 text-sm font-medium">Variable hygiene</h4>
        <Typography.Text type="secondary" className="text-xs">
          Scans every request URL, header, param, body, script, and auth config across this
          workspace for <code>{'{{key}}'}</code> and <code>pm.*.get/set()</code> usage.
        </Typography.Text>
      </div>

      <div className="flex items-center justify-between">
        <Segmented
          size="small"
          value={filter}
          onChange={(value) => setFilter(value as Filter)}
          options={[
            { label: `All (${data.entries.length})`, value: 'all' },
            { label: `Unused (${unusedCount})`, value: 'unused' },
            { label: `Duplicate keys (${duplicateCount})`, value: 'duplicate' },
          ]}
        />
      </div>

      <Table<VariableHygieneEntry>
        size="small"
        rowKey={(entry) => `${entry.scope}:${entry.scopeLabel}:${entry.key}`}
        dataSource={filtered}
        pagination={false}
        locale={{ emptyText: 'Nothing in this filter' }}
        columns={[
          {
            title: 'Scope',
            dataIndex: 'scopeLabel',
            width: 140,
            render: (label: string, entry) => (
              <Tag color={SCOPE_COLOR[entry.scope]} className="m-0">
                {label}
              </Tag>
            ),
          },
          {
            title: 'Key',
            dataIndex: 'key',
            render: (key: string) => <code className="text-xs">{key}</code>,
          },
          {
            title: 'Status',
            width: 220,
            render: (_, entry) => (
              <div className="flex flex-wrap gap-1">
                {!entry.isEnabled && <Tag className="m-0">Disabled</Tag>}
                {entry.isUnused && (
                  <Tag color="warning" className="m-0">
                    Unused
                  </Tag>
                )}
                {entry.alsoDefinedIn.length > 0 && (
                  <Tag
                    color="error"
                    className="m-0"
                    title={`Also defined in: ${entry.alsoDefinedIn.join(', ')}`}
                  >
                    Also in {entry.alsoDefinedIn.join(', ')}
                  </Tag>
                )}
                {!entry.isUnused && entry.alsoDefinedIn.length === 0 && (
                  <Tag color="success" className="m-0">
                    OK
                  </Tag>
                )}
              </div>
            ),
          },
        ]}
      />

      <div>
        <h4 className="mb-1 text-sm font-medium">Resolved values</h4>
        <Typography.Text type="secondary" className="mb-2 block text-xs">
          Effective value for each key given Global variables overridden by the active environment.
        </Typography.Text>
        {resolvedRows.length === 0 ? (
          <Alert
            type="info"
            showIcon
            title="No active environment"
            description="Activate an environment to preview resolved values."
          />
        ) : (
          <Table
            size="small"
            rowKey={([key]) => key}
            dataSource={resolvedRows}
            pagination={false}
            columns={[
              {
                title: 'Key',
                width: 180,
                render: ([key]: [string, { value: string | null; source: string }]) => (
                  <code className="text-xs">{key}</code>
                ),
              },
              {
                title: 'Value',
                render: ([, preview]: [string, { value: string | null; source: string }]) => (
                  <Input readOnly size="small" value={preview.value ?? ''} className="font-mono" />
                ),
              },
              {
                title: 'Source',
                width: 120,
                render: ([, preview]: [string, { value: string | null; source: string }]) => (
                  <Tag className="m-0">{preview.source}</Tag>
                ),
              },
            ]}
          />
        )}
      </div>
    </div>
  );
};
